import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, Team } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { getTeamsInAccountQueryKey } from 'queries/placeholder';
import { useAddTeamsToProjectMutation } from 'queries/project';
import { addProjectPlaceholder } from 'stores/memberships';

interface ReturnProps {
  addTeams: (projectId: number, teams: Team[], newTeam?: Team) => Promise<void>;
}

export const useAddTeams = (): ReturnProps => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutate: addTeamsToProject } = useAddTeamsToProjectMutation();

  const translate = useTranslate();
  const addTeamsTexts = useCallback(
    (count: number) => ({
      success: {
        title: translate('resource_management.add_placeholders.success.title', {
          count,
        }),
        message: translate(
          'resource_management.add_placeholders.success.message',
          { count },
        ),
      },
      error: {
        title: translate('resource_management.add_placeholders.error.title', {
          count,
        }),
        message: translate(
          'resource_management.add_placeholders.error.message',
          { count },
        ),
      },
    }),
    [translate],
  );

  const addTeams = useCallback(
    (projectId: number, _teams: Team[], newTeam?: Team) => {
      return new Promise<void>((resolve, reject) => {
        const count = newTeam == null ? _teams.length : _teams.length + 1;

        const handleSuccess = () => {
          _teams.forEach((team) => {
            dispatch(
              addProjectPlaceholder({
                projectId: ProjectId(projectId),
                team,
              }),
            );
          });
          resolve();
        };

        const handleError = () => {
          toastService(addTeamsTexts(count).error).error();
          reject();
        };

        if (newTeam != null) {
          queryClient.setQueryData<Team[]>(
            getTeamsInAccountQueryKey(),
            (oldValues = []) => [...oldValues, newTeam],
          );
        }

        const existingTeams = _teams.filter(
          ({ teamId }) => teamId !== newTeam?.teamId,
        );

        if (existingTeams.length > 0) {
          addTeamsToProject(
            {
              projectId,
              teamIds: existingTeams.map(({ teamId }) => teamId),
            },
            {
              onSuccess: () => {
                toastService(addTeamsTexts(count).success).success();
                handleSuccess();
              },
              onError: handleError,
            },
          );
        } else {
          handleSuccess();
        }
      });
    },
    [addTeamsTexts, addTeamsToProject, dispatch, queryClient],
  );

  return {
    addTeams,
  };
};
